import { useEffect, useRef, useState } from "react";
import { api, streamPost } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { DemoBadge } from "@/components/DemoBadge";
import { Bot, Send, Sparkles } from "lucide-react";

const SHORTCUTS = [
  "What is the current BTC/USD trend?",
  "Explain what RSI means.",
  "Compare BTC and ETH structure.",
  "What is a good risk per trade?",
];

export default function AppAnalyst() {
  const [assets, setAssets] = useState([]);
  const [symbol, setSymbol] = useState("BTC/USD");
  const [tf, setTf] = useState("4h");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => "sess-" + Math.random().toString(36).slice(2));
  const endRef = useRef(null);

  useEffect(() => { api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      await streamPost("/ai/assistant", { session_id: sessionId, message: q, symbol, timeframe: tf },
        (d) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d }; return c; }));
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "AI assistant temporarily unavailable." }; return c; });
    } finally { setBusy(false); }
  };

  const runAnalyst = async () => {
    if (busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: `Full analysis of ${symbol} on ${tf}` }, { role: "assistant", content: "" }]);
    try {
      await streamPost("/ai/analyst", { symbol, timeframe: tf },
        (d) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d }; return c; }));
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "AI analysis temporarily unavailable." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout title="AI Analyst">
      <div className="grid gap-6 lg:grid-cols-4">
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2"><Bot className="h-4 w-4 text-accent" /><span className="text-sm font-semibold">Context</span><DemoBadge /></div>
            <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Asset</label>
            <select value={symbol} onChange={(e) => setSymbol(e.target.value)} data-testid="analyst-symbol" className="mb-3 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm">
              {assets.map((a) => <option key={a.symbol} value={a.symbol}>{a.symbol}</option>)}
            </select>
            <label className="mb-1 block text-[10px] uppercase text-muted-foreground">Timeframe</label>
            <select value={tf} onChange={(e) => setTf(e.target.value)} data-testid="analyst-tf" className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm">
              {["15m", "1h", "4h", "1D"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={runAnalyst} disabled={busy} data-testid="analyst-run" className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              <Sparkles className="h-4 w-4" /> Full Analysis
            </button>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Prompt shortcuts</div>
            <div className="space-y-2">
              {SHORTCUTS.map((s) => <button key={s} onClick={() => send(s)} className="w-full rounded-lg border border-border px-3 py-2 text-left text-xs hover:bg-secondary">{s}</button>)}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 flex min-h-[600px] flex-col rounded-2xl border border-border bg-card">
          <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="analyst-messages">
            {messages.length === 0 && (
              <div className="flex h-full flex-col items-center justify-center text-center text-muted-foreground">
                <Bot className="h-10 w-10 text-accent" />
                <p className="mt-3 max-w-sm text-sm">Ask about trends, indicators or a specific asset. The analyst explains structured demo data — it never invents prices or news.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "border border-border bg-secondary/40"}`}>
                  {m.content || <span className="text-muted-foreground">…</span>}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-border p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the AI Analyst…" data-testid="analyst-input"
              className="flex-1 rounded-xl border border-border bg-background px-3.5 py-2.5 text-sm outline-none focus:border-primary" />
            <button disabled={busy} data-testid="analyst-send" className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground disabled:opacity-60"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
